import React, { useMemo } from 'react'
import ReactApexChart from 'react-apexcharts'
import { useWorkforce } from '../../../../../../../Context'
import { formatMonthLabel, parseMonthKey } from '../../../../../adapters/workforceDetailAdapter'

// Renders the per-workforce CLIK score history. Each point corresponds to a
// past CLIK enquiry. With a single enquiry this will be a lone marker — we
// still show it (per product call) so users see the latest score plotted in
// time even before repeat enquiries accumulate.
export default function CreditScoreTrend() {
  const { workforceDetailReport } = useWorkforce()
  const history = workforceDetailReport?.credit_summary?.credit_score_history ?? []

  const { categories, scores } = useMemo(() => {
    const sorted = [...history].sort((a, b) => parseMonthKey(a.date) - parseMonthKey(b.date))
    return {
      categories: sorted.map((d) => formatMonthLabel(d.date)),
      scores: sorted.map((d) => Number(d.score) || 0),
    }
  }, [history])

  const series = [{ name: 'Credit Score', data: scores }]

  const options = {
    chart: {
      type: 'area',
      height: 250,
      toolbar: { show: false },
      zoom: { enabled: false },
    },
    colors: ['#14378E'],
    dataLabels: { enabled: false },
    stroke: {
      curve: 'smooth',
      width: 2,
    },
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.15,
        opacityTo: 0.02,
        stops: [0, 90, 100],
      },
    },
    // Always show markers so a 1-point history still renders as a visible dot.
    markers: {
      size: scores.length === 1 ? 6 : 4,
      strokeWidth: 0,
      hover: { sizeOffset: 2 },
    },
    grid: {
      borderColor: '#F2F4F7',
      strokeDashArray: 0,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
      padding: { top: 0, right: 0, bottom: 0, left: 10 },
    },
    xaxis: {
      categories,
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: {
        rotate: 0,
        hideOverlappingLabels: true,
        style: {
          colors: '#667085',
          fontSize: '12px',
          fontFamily: 'Inter',
          fontWeight: 400,
        },
      },
    },
    // CLIK score band is 0–1000 (per Figma). Hardcoded so the y-axis stays
    // stable across subjects regardless of their score values.
    yaxis: {
      min: 0,
      max: 1000,
      tickAmount: 5,
      labels: {
        style: {
          colors: '#667085',
          fontSize: '12px',
          fontFamily: 'Inter',
          fontWeight: 400,
        },
        formatter: (val) => Number(val).toLocaleString('en-US'),
      },
    },
    legend: { show: false },
    tooltip: {
      theme: 'light',
      x: { show: true },
      y: {
        formatter: (val) => `${Number(val).toLocaleString('en-US')}`,
      },
    },
  }

  return (
    <div className="w-full relative">
      <div className="absolute left-[-45px] top-1/2 -translate-y-1/2 rotate-[-90deg] whitespace-nowrap text-xs font-medium text-gray-500">
        Historical score
      </div>
      <div className="pl-4">
        <ReactApexChart options={options} series={series} type="area" height={250} />
      </div>
      <div className="w-full text-center text-xs font-medium text-gray-500 mt-2">Month</div>
    </div>
  )
}
